import { type HTMLAttributes, type TdHTMLAttributes, type ThHTMLAttributes, forwardRef } from 'react'

import { cn } from '@/utils/cn'
import { type VariantProps, cva } from 'class-variance-authority'
import { Link, type LinkProps } from 'react-router'

export const Table = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(({ className, ...props }, ref) => (
  <table ref={ref} className={cn('w-full caption-bottom overflow-hidden text-sm', className)} {...props} />
))
Table.displayName = 'Table'

export const TableHeader = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <thead ref={ref} className={cn(className)} {...props} />
)
TableHeader.displayName = 'TableHeader'

export const TableBody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <tbody ref={ref} className={cn(className)} {...props} />
)
TableBody.displayName = 'TableBody'

const tableRowVariants = cva('px-4 transition-colors', {
  variants: {
    variant: {
      header: 'bg-page',
      row: 'translate-x-0 translate-y-0 odd:bg-page even:bg-surface',
      default: '',
    },
  },
  defaultVariants: {
    variant: 'row',
  },
})

export const TableRow = forwardRef<
  HTMLTableRowElement,
  HTMLAttributes<HTMLTableRowElement> & VariantProps<typeof tableRowVariants>
>(({ className, variant, ...props }, ref) => (
  <tr ref={ref} className={cn(tableRowVariants({ variant, className }))} {...props} />
))
TableRow.displayName = 'TableRow'

const defaultCellStyles = cn('px-3 py-2 align-middle')

export const TableHead = forwardRef<HTMLTableCellElement, ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th ref={ref} className={cn(defaultCellStyles, 'h-10 border-b text-left font-medium', className)} {...props} />
  )
)
TableHead.displayName = 'TableHead'

export const TableCell = forwardRef<HTMLTableCellElement, TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => <td ref={ref} className={cn(defaultCellStyles, className)} {...props} />
)
TableCell.displayName = 'TableCell'

export const TableRowLink = forwardRef<HTMLAnchorElement, LinkProps>(({ className, ...props }, ref) => (
  <Link
    ref={ref}
    data-role="table-row-link"
    className={cn(
      'outline-none transition-colors',
      'before:absolute before:top-0 before:left-0 before:h-full before:w-full before:bg-transparent before:transition-colors',
      'hocus:text-accent hocus:before:bg-accent/10',
      className
    )}
    {...props}
  />
))
